'use client';

import { useState, useEffect } from 'react';
import { ThemedImage } from '@/components/ui/themed-image';
import { Star, Gift, CheckCircle, Info, Sparkle } from '@phosphor-icons/react';
import { PromotionModal } from './promotion-modal';
import { getProductById, type ProductWithCategories } from '@/lib/api/products';
import { useMoneyRules } from '@/lib/money-rules';

// id ролла за первый заказ здесь БОЛЬШЕ НЕ ЗАШИТ: он приходит с сервера
// (GET /api/money-rules → gift_products) и меняется в админке при смене
// поставки. Копия «77» в этом файле означала бы, что после замены подарка
// модалка показывает картинку и название старого ролла.

interface FirstOrderModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Условия акции с сервера (site_promotion_terms). Не передали или пришёл
   * пустой список — показываем зашитый текст ниже: пустой блок «Условия акции»
   * хуже устаревшего, потому что это публичная оферта.
   */
  terms?: readonly string[];
}

/** Условия на 16.08.2026 — запасной текст, если сервер не ответил. */
const FALLBACK_TERMS = [
  'Акция действует только для новых клиентов (первый заказ)',
  'Минимальная сумма заказа — 1200₽',
  'Необходима авторизация',
  'Подарок добавляется автоматически при первом заказе',
  'Детали подарка могут различаться и уточняются при оформлении заказа',
  'Акция суммируется с другими акциями',
  'Ролл за отзыв и ролл за первый заказ не суммируются между собой',
  'Сет дня — отдельная акция и не учитывается в сумме для подарка',
  'Товар, выигранный в рулетке, не учитывается в сумме для подарка',
  // Категория 15 «Роллы -30%» не входит в сумму для подарка в самом расчёте
  // (azia_back: routers/orders.js). Пункт есть и в site_promotion_terms —
  // запасной текст обязан совпадать со строкой базы.
  'Товары из категории «Роллы -30%» не учитываются в сумме для подарка',
  'Действует только при онлайн-заказе',
  'В сумму чека включена стоимость только выбранных вами блюд. Обратите внимание: оплата доставки, приборов и соусов рассчитывается отдельно и не включается в общую сумму чека.',
];

export function FirstOrderModal({ open, onClose, terms }: FirstOrderModalProps) {
  const [firstOrderRoll, setFirstOrderRoll] = useState<ProductWithCategories | null>(null);
  const firstOrderRollId = useMoneyRules().first_order.product_id;

  useEffect(() => {
    if (!open) return;
    getProductById(firstOrderRollId, true)
      .then(setFirstOrderRoll)
      .catch(() => setFirstOrderRoll(null));
  }, [open, firstOrderRollId]);

  return (
    <PromotionModal
      open={open}
      onClose={onClose}
      title="Подарок за первый заказ"
      icon={<Star className="w-5 h-5 md:w-6 md:h-6 text-white" weight="fill" />}
    >
      <div className="px-4 pt-5 pb-4 md:px-6 md:pt-6 md:pb-6 space-y-6 md:space-y-8">
        {/* Hero Image */}
        <div className="relative w-full h-48 md:h-64 rounded-card overflow-hidden border-2 border-[var(--border-default)] bg-[var(--surface-tertiary)]">
          <ThemedImage
            src={firstOrderRoll?.image_url}
            alt={firstOrderRoll?.name || 'Подарок за первый заказ'}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, 80vw"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-elevated)]/80 via-transparent to-transparent" />
        </div>

        {/* Description */}
        <div className="p-4 md:p-6 rounded-card bg-gradient-to-br from-[var(--primary-subtle)] to-[var(--primary-muted)] border border-[var(--primary)]/20">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-card bg-[var(--primary)] flex items-center justify-center flex-shrink-0">
              <Sparkle className="w-5 h-5 text-white" weight="fill" />
            </div>
            <div className="flex-1">
              <h3 className="text-base md:text-lg font-bold font-heading text-[var(--text-primary)] mb-2">
                Добро пожаловать!
              </h3>
              <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed">
                Делаете первый заказ? Получите специальный подарок от нас! 
                Это наш способ сказать "спасибо" за то, что выбрали нас.
              </p>
            </div>
          </div>
        </div>

        {/* How it works */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-5 h-5 text-[var(--primary)]" weight="fill" />
            <h3 className="text-base md:text-lg font-bold font-heading text-[var(--text-primary)]">
              Как получить подарок?
            </h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-card bg-[var(--surface-secondary)] border border-[var(--border-default)]">
              <div className="w-6 h-6 rounded-pill bg-[var(--primary)] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-white">1</span>
              </div>
              <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed flex-1">
                Зарегистрируйтесь в приложении или на сайте
              </p>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-card bg-[var(--surface-secondary)] border border-[var(--border-default)]">
              <div className="w-6 h-6 rounded-pill bg-[var(--primary)] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-white">2</span>
              </div>
              <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed flex-1">
                Оформите свой первый заказ
              </p>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-card bg-[var(--surface-secondary)] border border-[var(--border-default)]">
              <div className="w-6 h-6 rounded-pill bg-[var(--primary)] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-white">3</span>
              </div>
              <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed flex-1">
                Получите подарок автоматически при оформлении заказа
              </p>
            </div>
          </div>
        </div>

        {/* Conditions */}
        <div>
          <h3 className="text-base md:text-lg font-bold font-heading text-[var(--text-primary)] mb-3">
            Условия акции
          </h3>
          <div className="space-y-2">
            {(terms && terms.length > 0 ? terms : FALLBACK_TERMS).map((condition, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-pill bg-[var(--primary-subtle)] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-3 h-3 text-[var(--primary)]" weight="fill" />
                </div>
                <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed flex-1">
                  {condition}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Highlight */}
        <div className="p-4 md:p-6 rounded-card bg-gradient-to-r from-[var(--primary-subtle)] to-[var(--primary-muted)] border border-[var(--primary)]/20">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-card bg-[var(--primary)] flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" weight="fill" />
            </div>
            <div>
              <p className="text-base md:text-lg text-[var(--text-primary)] font-bold mb-1">
                Сделайте первый заказ и получите подарок!
              </p>
              <p className="text-sm text-[var(--text-secondary)]">
                Мы ценим каждого нового клиента и хотим сделать ваш первый опыт незабываемым
              </p>
            </div>
          </div>
        </div>
      </div>
    </PromotionModal>
  );
}
